import { UiButtonComponent } from 'src/app/shared/ui';
import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { MyUserService } from 'src/app/core/services/my-user/my-user.service';
import { UserInfoInterface } from 'src/app/core/interfaces';
import { UserBackendService } from 'src/app/core/backend-services/user/user.backend-service';

/** bump for the next announcement; stored per account as settings.whatsNewSeen */
export const WHATS_NEW_VERSION = '2026-11-design';
/** accounts created after this date never saw the old design → no "what's new" */
export const WHATS_NEW_RELEASED_AT = new Date('2026-11-03T00:00:00+01:00');
/**
 * One-time "Was ist neu" dialog after the design release. Shown once per account (the
 * seen version lives in the account settings on the server) to players who knew the old
 * design, never on the game route (no interruption mid-round).
 */
@Component({
  selector: 'app-whats-new',
  standalone: true,
  imports: [UiButtonComponent, RouterModule],
  templateUrl: './whats-new.component.html',
  styleUrl: './whats-new.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WhatsNewComponent implements OnInit {
  private myUser = inject(MyUserService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private userBackend = inject(UserBackendService);

  readonly open = signal(false);
  private user: UserInfoInterface | null = null;
  /** seen version from the account settings; undefined = not loaded (yet) for this user */
  private seenVersion: string | null | undefined = undefined;
  private loadingFor?: number;

  ngOnInit(): void {
    this.myUser.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      if (user?.userID !== this.user?.userID) this.seenVersion = undefined; // other account
      this.user = user;
      this.maybeOpen();
    });
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        // navigated into a game while open (e.g. a lobby link): hide, show again later
        if (this.open() && isGameRoute(this.router.url)) {
          this.open.set(false);
          return;
        }
        this.maybeOpen();
      });
  }

  close(): void {
    this.open.set(false);
    this.seenVersion = WHATS_NEW_VERSION;
    // the server merges this key into the existing settings
    this.userBackend.updateSettings({ whatsNewSeen: WHATS_NEW_VERSION }).catch((e) =>
      console.error('Could not store whatsNewSeen:', e),
    );
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open()) this.close();
  }

  private maybeOpen(): void {
    // before the first navigation router.url is still "/" (e.g. /spielen opened directly)
    if (!this.router.navigated) return;
    if (this.open() || !this.user) return;
    // decide without the seen version first, so guests and new accounts never ask the server
    if (!shouldShowWhatsNew(this.user, this.router.url, null)) return;
    if (this.seenVersion === undefined) {
      this.loadSeenVersion(this.user);
      return;
    }
    if (shouldShowWhatsNew(this.user, this.router.url, this.seenVersion)) this.open.set(true);
  }

  private async loadSeenVersion(user: UserInfoInterface): Promise<void> {
    if (this.loadingFor === user.userID) return;
    this.loadingFor = user.userID;
    try {
      const res = await this.userBackend.getSettings();
      if (this.user?.userID !== user.userID) return; // switched account meanwhile
      this.seenVersion = res?.settings?.whatsNewSeen ?? null;
      this.maybeOpen();
    } catch {
      // not loaded: stay closed, the next navigation tries again
    } finally {
      this.loadingFor = undefined;
    }
  }
}

/** pure decision, exported for tests */
export function shouldShowWhatsNew(
  user: Pick<UserInfoInterface, 'registrationDate' | 'isGuest'> | null,
  url: string,
  seenVersion: string | null,
): boolean {
  if (!user || user.isGuest || seenVersion === WHATS_NEW_VERSION) return false;
  if (isGameRoute(url)) return false;
  const registered = user.registrationDate ? new Date(user.registrationDate) : null;
  return !!registered && !isNaN(registered.getTime()) && registered < WHATS_NEW_RELEASED_AT;
}

function isGameRoute(url: string): boolean {
  return /^\/(spielen|spiel)(\/|\?|#|$)/.test(url);
}
